import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurricula, useUserEnrollments, useEnrollInCurriculum } from '@/hooks/useCurriculum';
import { useDisciplines } from '@/hooks/useDisciplines';
import { 
  BookOpen, 
  Users, 
  Clock, 
  Award, 
  Play, 
  CheckCircle,
  Pause,
  GraduationCap,
  TrendingUp
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const CurriculumManager: React.FC = () => {
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('all');

  const { data: curricula, isLoading: curricLoading } = useCurricula(
    selectedDiscipline === 'all' ? undefined : selectedDiscipline,
    selectedLevel === 'all' ? undefined : selectedLevel
  );
  const { data: enrollments } = useUserEnrollments();
  const { data: disciplines } = useDisciplines();
  const { mutate: enrollInCurriculum, isPending: enrolling } = useEnrollInCurriculum();

  // Filter out any disciplines that might have empty or invalid IDs
  const validDisciplines = disciplines?.filter(discipline => discipline.id && discipline.id.trim() !== '') || [];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-white text-green-800';
      case 'intermediate': return 'bg-white text-black';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-white text-black';
      case 'completed': return 'bg-white text-green-800';
      case 'paused': return 'bg-white text-black';
      case 'dropped': return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'dropped': return <Pause className="h-4 w-4" />;
      default: return <BookOpen className="h-4 w-4" />;
    }
  };

  const isEnrolled = (curriculumId: string) => {
    return enrollments?.some(e => e.curriculum_id === curriculumId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-6 w-6" />
        <h2 className="text-2xl font-bold">Learning Curricula</h2>
      </div>

      <Tabs defaultValue="available" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="available">Available Curricula</TabsTrigger>
          <TabsTrigger value="enrolled">My Enrollments</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Level</label>
                  <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder="All levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All levels</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Discipline</label>
                  <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                    <SelectTrigger>
                      <SelectValue placeholder="All disciplines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All disciplines</SelectItem>
                      {validDisciplines.map((discipline) => (
                        <SelectItem key={discipline.id} value={discipline.id}>
                          {discipline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSelectedLevel('all');
                      setSelectedDiscipline('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Curricula Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {curricLoading ? (
              Array(6).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-white rounded mb-4"></div>
                    <div className="h-3 bg-white rounded mb-2"></div>
                    <div className="h-3 bg-white rounded mb-4"></div>
                    <div className="h-8 bg-white rounded"></div>
                  </CardContent>
                </Card>
              ))
            ) : curricula && curricula.length > 0 ? (
              curricula.map((curriculum) => (
                <Card key={curriculum.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg line-clamp-2">{curriculum.title}</CardTitle>
                      <Badge className={getLevelColor(curriculum.level)}>
                        {curriculum.level}
                      </Badge>
                    </div>
                    {curriculum.disciplines && (
                      <Badge variant="outline" className="w-fit">
                        {curriculum.disciplines.name}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-black line-clamp-3">
                      {curriculum.description}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-black">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {curriculum.estimated_duration_hours}h
                      </div>
                      {curriculum.certification_available && (
                        <div className="flex items-center gap-1">
                          <Award className="h-4 w-4" />
                          Certificate
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-black">
                        by {curriculum.profiles?.full_name || curriculum.profiles?.username}
                      </span>
                      <Button
                        onClick={() => enrollInCurriculum(curriculum.id)}
                        disabled={enrolling || isEnrolled(curriculum.id)}
                        size="sm"
                      >
                        {isEnrolled(curriculum.id) ? 'Enrolled' : 'Enroll'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="col-span-full">
                <CardContent className="text-center py-8">
                  <BookOpen className="h-12 w-12 text-black mx-auto mb-4" />
                  <p className="text-black">No curricula found matching your filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="enrolled" className="space-y-6">
          {enrollments && enrollments.length > 0 ? (
            <div className="space-y-4">
              {enrollments.map((enrollment) => (
                <Card key={enrollment.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {getStatusIcon(enrollment.status)}
                          {enrollment.learning_curricula?.title}
                        </CardTitle>
                        <p className="text-sm text-black mt-1">
                          Enrolled {formatDistanceToNow(new Date(enrollment.enrollment_date), { addSuffix: true })}
                        </p>
                      </div>
                      <Badge className={getStatusColor(enrollment.status)}>
                        {enrollment.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Progress</span>
                      <span className="text-sm text-black">
                        {enrollment.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${enrollment.progress_percentage}%` }}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-black">Level: </span>
                        <Badge className={getLevelColor(enrollment.learning_curricula?.level || '')} variant="outline">
                          {enrollment.learning_curricula?.level}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-black">Duration: </span>
                        {enrollment.learning_curricula?.estimated_duration_hours}h
                      </div>
                    </div>

                    {enrollment.status === 'completed' && enrollment.completion_date && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Completed {formatDistanceToNow(new Date(enrollment.completion_date), { addSuffix: true })}
                      </div>
                    )}

                    <Button className="w-full" variant="outline">
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Continue Learning
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Users className="h-12 w-12 text-black mx-auto mb-4" />
                <p className="text-black">You haven't enrolled in any curricula yet</p>
                <Button className="mt-4" onClick={() => window.location.hash = '#available'}>
                  Browse Available Curricula
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CurriculumManager;
